import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export const AuthShell = ({ title, sub, children, testid }) => (
  <div className="flex min-h-[calc(100vh-4rem)] items-start justify-center bg-gradient-to-br from-[#EFE9FB] to-[#FFF0EE] px-4 py-12" data-testid={testid}>
    <div className="fade-up w-full max-w-md rounded-3xl bg-white p-7 shadow-[0_20px_60px_-20px_rgba(59,18,114,0.3)] sm:p-9">
      <h1 className="text-2xl font-bold text-[#1E1B2E] sm:text-3xl">{title}</h1>
      {sub && <p className="mt-1.5 text-sm text-[#64748B]">{sub}</p>}
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

export const GoogleButton = () => {
  const { t } = useI18n();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-btn" className="flex w-full items-center justify-center gap-3 rounded-full border border-[#E2E8F0] bg-white py-3 text-sm font-semibold transition-colors hover:bg-[#F8F9FC]">
      <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {t("auth.google")}
    </button>
  );
};

export const LoginPage = () => {
  const { t } = useI18n();
  const { signedIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", form);
      signedIn(data);
      nav(loc.state?.from || "/account", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };
  return (
    <AuthShell title={t("auth.loginTitle")} sub={t("auth.loginSub")} testid="login-page">
      <GoogleButton />
      <div className="my-5 flex items-center gap-3 text-xs text-[#94A3B8]"><span className="h-px flex-1 bg-[#EEF0F6]" />{t("auth.or")}<span className="h-px flex-1 bg-[#EEF0F6]" /></div>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="label">{t("auth.email")}</label><input type="email" required className="field" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} data-testid="login-email-input" /></div>
        <div><label className="label">{t("auth.password")}</label><input type="password" required className="field" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" /></div>
        {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
        <button disabled={busy} className="btn-violet w-full !py-3" data-testid="login-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("auth.login")}</button>
      </form>
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/password-dimenticata" className="font-semibold text-[#3B1272]" data-testid="forgot-password-link">{t("auth.forgot")}</Link>
        <Link to="/registrati" className="font-semibold text-[#FF7A6B]" data-testid="register-link">{t("auth.register")}</Link>
      </div>
    </AuthShell>
  );
};

export const ForgotPage = () => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setDone(true); } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <AuthShell title={t("auth.forgotTitle")} sub={t("auth.forgotSub")} testid="forgot-page">
      {done ? <p className="rounded-xl bg-[#EFE9FB] p-4 text-sm" data-testid="forgot-done">{t("auth.forgotDone")}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <input type="email" required className="field" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="btn-violet w-full !py-3" data-testid="forgot-submit-btn">{t("auth.sendLink")}</button>
        </form>
      )}
    </AuthShell>
  );
};

export const ResetPage = () => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw }); setDone(true); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthShell title={t("auth.resetTitle")} sub={t("auth.pwHint")} testid="reset-page">
      {done ? <div><p className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="reset-done">{t("auth.resetDone")}</p><Link to="/accedi" className="btn-violet mt-4 w-full">{t("auth.login")}</Link></div> : (
        <form onSubmit={submit} className="space-y-4">
          <input type="password" required minLength={8} className="field" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          {err && <p className="text-sm text-red-600" data-testid="reset-error">{err}</p>}
          <button className="btn-violet w-full !py-3" data-testid="reset-submit-btn">{t("auth.resetBtn")}</button>
        </form>
      )}
    </AuthShell>
  );
};

export const VerifyEmailPage = () => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const { refreshMe } = useAuth();
  const [state, setState] = useState("loading");
  const [err, setErr] = useState("");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState("ok"); refreshMe(); })
      .catch((e) => { setErr(errMsg(e)); setState("error"); });
  }, [params, refreshMe]);
  return (
    <AuthShell title={t("auth.verifyTitle")} testid="verify-email-page">
      {state === "loading" && <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t("auth.verifying")}</p>}
      {state === "ok" && <p className="flex items-center gap-2 text-emerald-700" data-testid="verify-success"><CheckCircle2 className="h-5 w-5" />{t("auth.verified")}</p>}
      {state === "error" && <p className="flex items-center gap-2 text-red-600" data-testid="verify-error"><XCircle className="h-5 w-5" />{err}</p>}
      <Link to="/" className="btn-violet mt-6 w-full">Juppo</Link>
    </AuthShell>
  );
};

export const AuthCallback = () => {
  const { t } = useI18n();
  const { signedIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", loc.pathname);
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { signedIn(data); nav(loc.pathname || "/account", { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); nav("/accedi", { replace: true }); });
  }, [loc, nav, signedIn]);
  return <div className="flex min-h-[60vh] items-center justify-center gap-2" data-testid="auth-callback"><Loader2 className="h-5 w-5 animate-spin" />{t("auth.checking")}</div>;
};
